import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";
import * as AuthActions from 'src/app/store/auth/auth.actions';
import { selectAccountIds, selectSchoolAccountId, selectUserAccountId, selectUserAuthentication, selectUserAuthenticationError, selectUserAuthenticationLoading, selectUserToken, selectUserTokenError, selectUserTokenLoading } from "./auth.selectors";
import { UserAuthentication, UserToken } from "@core/models/domain/auth.domain";
import { AppState } from "../app.reducer";
import { LoginRequest, CreateSchoolAccountRequest, ValidateCodeRequest, ResetPasswordRequest, SetPasswordRequest, ChangePasswordRequest } from 'src/app/core/models/request/auth.request';

@Injectable({ providedIn: 'root' })
export class AuthStoreFacade {
    // Authentication Observables
    userAuthentication$: Observable<UserAuthentication> = this.store.select(selectUserAuthentication);
    authError$: Observable<string> = this.store.select(selectUserAuthenticationError);
    authLoading$: Observable<boolean> = this.store.select(selectUserAuthenticationLoading);

    // Account ID Observables (source of truth for request building)
    userAccountId$: Observable<string | null> = this.store.select(selectUserAccountId);
    schoolAccountId$: Observable<string | null> = this.store.select(selectSchoolAccountId);

    // Token Observables
    userToken$: Observable<UserToken> = this.store.select(selectUserToken);
    tokenError$: Observable<string> = this.store.select(selectUserTokenError);
    tokenLoading$: Observable<boolean> = this.store.select(selectUserTokenLoading);
    
    constructor(private store: Store<AppState>) {}

    /**
     * Synchronous snapshot of the in-memory account IDs, for imperative save handlers.
     * The NgRx store emits its current value synchronously, so this resolves immediately.
     */
    getAccountIdsSnapshot(): { userAccountId: string | null; schoolAccountId: string | null } {
        let ids: { userAccountId: string | null; schoolAccountId: string | null } = {
            userAccountId: null,
            schoolAccountId: null,
        };
        this.store.select(selectAccountIds).pipe(take(1)).subscribe((value) => (ids = value));
        return ids;
    }

    login(request: LoginRequest): void {
        this.store.dispatch(AuthActions.login({ request }));
    }

    createSchoolAccount(request: CreateSchoolAccountRequest): void {
        this.store.dispatch(AuthActions.createSchoolAccount({ request }));
    }

    resendValidationCode(): void {
        this.store.dispatch(AuthActions.resendValidationCode());
    }

    validateCode(request: ValidateCodeRequest): void {
        this.store.dispatch(AuthActions.validateCode({ request }));
    }

    requestPasswordReset(request: ResetPasswordRequest): void {
        this.store.dispatch(AuthActions.requestPasswordReset({ request }));
    }

    setPassword(request: SetPasswordRequest): void {
        this.store.dispatch(AuthActions.setPassword({ request }));
    }

    changePassword(request: ChangePasswordRequest): void {
        this.store.dispatch(AuthActions.changePassword({ request }));
    }

    logout(): void {
        this.store.dispatch(AuthActions.logout());
    }

    refreshToken(): void {
        this.store.dispatch(AuthActions.refreshToken());
    }

    /** Re-hydrate the session from the backend (restores account IDs into the store). */
    hydrateAuth(): void {
        this.store.dispatch(AuthActions.hydrateAuth());
    }
}